import { Injectable, computed, effect, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CartService } from './cart.service';
import { ComboRegistryService } from './combo-registry.service';
import { ToastService } from './toast.service';

export interface ComboPendingMessage {
  groupId: string;
  subId: string;
  text: string;
}

// Per-combo-group validity, derived entirely from the cart and the registry -
// no component wires this up per section view, so it holds true regardless of
// where a combo line came from (menu, search, a restored cart) and however many
// combo groups are active on the menu at once.
@Injectable({ providedIn: 'root' })
export class ComboValidationService {
  private readonly cartService = inject(CartService);
  private readonly registry = inject(ComboRegistryService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  // Item counts per subsection, counting only cart lines whose (item, subsection)
  // resolves to a known combo slot - a line added outside any combo context
  // never contributes here.
  private readonly counts = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const line of this.cartService.cartLines()) {
      if (!line.subSectionId || !this.registry.resolveLine(line.item.id, line.subSectionId)) continue;
      counts[line.subSectionId] = (counts[line.subSectionId] ?? 0) + line.quantity;
    }
    return counts;
  });

  // One message per unmet requirement or overflow, across every combo group the
  // guest has actually started - a combo nobody has touched yet stays silent.
  readonly pendingMessages = computed<ComboPendingMessage[]>(() => {
    const counts = this.counts();
    const messages: ComboPendingMessage[] = [];

    for (const group of this.registry.getGroups()) {
      const requiredCounts = group.requiredSubIds.map(id => counts[id] ?? 0);
      const optionalCounts = group.optionalSubIds.map(id => counts[id] ?? 0);
      const active = requiredCounts.some(count => count > 0) || optionalCounts.some(count => count > 0);
      if (!active) continue;

      // The highest required count reached so far is the shared combo count every
      // required slot must match, and every optional slot is capped against.
      const target = Math.max(1, ...requiredCounts);

      for (const subId of group.requiredSubIds) {
        const have = counts[subId] ?? 0;
        if (have >= target) continue;

        const missing = target - have;
        messages.push({
          groupId: group.groupId,
          subId,
          text: this.translate.instant('COMBO.ADD_N', { count: missing, section: group.subNames[subId] }),
        });
      }

      for (const subId of group.optionalSubIds) {
        const have = counts[subId] ?? 0;
        if (have <= target) continue;

        messages.push({
          groupId: group.groupId,
          subId,
          text: this.translate.instant('COMBO.REMOVE_N', { count: have - target, section: group.subNames[subId] }),
        });
      }
    }

    return messages;
  });

  readonly isAllValid = computed(() => this.pendingMessages().length === 0);

  // Groups the guest has actually touched - used only to tell "just became valid" apart
  // from "emptied back out to nothing," which also clears its pending messages but isn't
  // a completion worth celebrating.
  private readonly activeGroupIds = computed<Set<string>>(() => {
    const counts = this.counts();
    const ids = new Set<string>();
    for (const group of this.registry.getGroups()) {
      const active = group.requiredSubIds.some(id => (counts[id] ?? 0) > 0)
        || group.optionalSubIds.some(id => (counts[id] ?? 0) > 0);
      if (active) ids.add(group.groupId);
    }
    return ids;
  });

  // Not itself reactive state - just remembers what pendingMessages() looked like on the
  // previous run, so the effect below can tell "still incomplete" apart from "just fixed".
  private previousIncompleteGroupIds = new Set<string>();

  constructor() {
    effect(() => {
      const incompleteNow = new Set(this.pendingMessages().map(message => message.groupId));
      const active = this.activeGroupIds();

      for (const groupId of this.previousIncompleteGroupIds) {
        if (!active.has(groupId) || incompleteNow.has(groupId)) continue;

        const name = this.registry.getGroups().find(group => group.groupId === groupId)?.name ?? '';
        this.toastService.success(this.translate.instant('COMBO.COMPLETE', { combo: name }));
      }

      this.previousIncompleteGroupIds = incompleteNow;
    });
  }

  // Deliberately generic - the guide block above already spells out exactly what's
  // missing or excess per combo, so the checkout-time toast just points there instead
  // of repeating (and potentially only showing) one of several active issues.
  checkoutBlockedMessage(): string {
    return this.translate.instant('COMBO.CANNOT_PROCEED');
  }
}
